import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ResmoMascot from "../components/ResmoMascot";
import ProMoInstructions from "../components/ProMoInstructions";
import PasswordInput from "../components/PasswordInput";
import PasswordStrengthMeter from "../components/PasswordStrengthMeter";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { useTheme } from "../context/ThemeContext";
import { useBetaGate } from "../context/BetaGateContext";
import { useCopy } from "../lib/copy";
import Navbar from "../components/Navbar";

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const { isPro } = useTheme();
  const { betaEmail } = useBetaGate();
  const t = useCopy();
  const [name, setName] = useState("");
  // Pre-fill from the gate-cookie email so testers don't retype it.
  // Registration is enforced to match the invite email server-side, so locking
  // this field also prevents a confusing "email mismatch" error.
  const [email, setEmail] = useState(betaEmail || "");
  const emailLocked = !!betaEmail;
  const [password, setPassword] = useState("");
  // Confirm-password field — testers must type their password twice on first
  // visit so we catch typos before they're locked out of their fresh account.
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Derived state: only the LIVE mismatch warning shown under the confirm
  // field once the user has typed something there. We keep the post-submit
  // `error` separate so server errors don't get clobbered.
  const passwordsMismatch = confirmPassword.length > 0 && confirmPassword !== password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    // Client-side guard — must run BEFORE we burn a server call. Match check
    // is case-sensitive and exact (passwords are stored as-is, so any
    // mismatch here would lock the user out on their next login).
    if (password !== confirmPassword) {
      setError("Passwords don't match. Type the same password in both fields.");
      return;
    }
    setLoading(true);
    const ok = await register(name, email, password);
    setLoading(false);
    if (ok) {
      // Honor the Welcome-page Quick-Start door pick (if any) so the user
      // lands directly on the tool they wanted instead of the generic
      // dashboard. sessionStorage entries are consumed (one-shot).
      // Otherwise, brand-new users get the guided Resmo onboarding
      // (unless the admin has turned it off).
      let next = "/onboarding";
      try {
        const cfg = await api.get("/onboarding/config");
        if (cfg.data?.enabled === false) next = "/welcome";
      } catch { /* default to onboarding */ }
      try {
        const stash = sessionStorage.getItem("resmo:postAuthNext");
        if (stash) {
          next = stash;
          sessionStorage.removeItem("resmo:postAuthNext");
          sessionStorage.removeItem("resmo:postAuthIntent");
        }
      } catch { /* sessionStorage blocked */ }
      navigate(next);
    } else {
      setError("Could not register. Try a different email or longer password.");
    }
  };

  return (
    <div className="min-h-screen rm-paper">
      <Navbar />
      <div className="max-w-5xl mx-auto px-4 md:px-8 py-12 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
        <div className="hidden md:flex flex-col items-center justify-center">
          {isPro ? (
            <ProMoInstructions
              tag="Get started"
              title="Three steps to your first tailored output."
              steps={[
                "Create your account — no credit card needed.",
                "Upload your resume (PDF, DOCX, or paste it in).",
                "Paste any job description — receive a tailored resume, fit assessment, and salary intel.",
              ]}
              footnote="Free to start. Senior-grade tailoring tuned for Director, VP, Principal, and Staff roles."
              testId="promo-register-instructions"
            />
          ) : (
            <>
              <ResmoMascot state="idle" size={260} />
              <div className="font-display text-2xl mt-4 uppercase text-center">
                {t(<>Hello, snack.<br />Let's get you hired.</>, <>Welcome.<br />Let's get you hired.</>)}
              </div>
            </>
          )}
        </div>

        <div className="rm-card-static p-8 md:p-10">
          <div className="rm-tag mb-3">Sign up</div>
          <h1 className="font-display text-4xl uppercase mb-6">{t("Make Resmo yours.", "Create your account.")}</h1>
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="register-form">
            <div>
              <label className="rm-label block mb-2">Name</label>
              <input
                type="text"
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="rm-input"
                data-testid="register-name-input"
                placeholder="Your name"
              />
            </div>
            <div>
              <label className="rm-label block mb-2">
                Email
                {emailLocked && (
                  <span className="ml-2 rm-tag text-[9px]"
                        style={{ background: "#FFC145", borderColor: "#0F1B2D", color: "#0F1B2D" }}>
                    From your invite — locked
                  </span>
                )}
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rm-input"
                data-testid="register-email-input"
                placeholder="you@email.com"
                readOnly={emailLocked}
                style={emailLocked ? { background: "#FFFBEB", cursor: "not-allowed" } : undefined}
              />
              {emailLocked && (
                <div className="text-[11px] font-mono-rm mt-1.5" style={{ color: "#475569" }}>
                  Your invite was issued to this address. Use a different email?{" "}
                  <Link to="/gate" className="underline font-bold" data-testid="register-change-invite">
                    Enter a different invite
                  </Link>
                </div>
              )}
            </div>
            <div>
              <label className="rm-label block mb-2" htmlFor="register-password">Password</label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="At least 6 characters"
                minLength={6}
                autoComplete="new-password"
                testId="register-password-input"
                ariaLabel="Password"
              />
              <PasswordStrengthMeter password={password} />
            </div>
            <div>
              <label className="rm-label block mb-2" htmlFor="register-confirm-password">
                Confirm password
              </label>
              <PasswordInput
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Type your password again"
                minLength={6}
                autoComplete="new-password"
                testId="register-confirm-password-input"
                ariaLabel="Confirm password"
              />
              {/* Live mismatch warning — shows only after the user has typed
                  something in the confirm field, so the form doesn't yell
                  while they're still mid-type. */}
              {passwordsMismatch && (
                <div
                  className="text-[11px] font-mono-rm mt-1.5"
                  style={{ color: "#B91C1C", fontWeight: 700 }}
                  data-testid="register-password-mismatch"
                >
                  Passwords don&apos;t match yet.
                </div>
              )}
            </div>
            {error && (
              <div className="font-mono-rm text-sm p-3 rounded-md" style={{ background: "#FEF2F2", border: "3px solid #EF4444", color: "#991B1B" }} data-testid="register-error">
                {error}
              </div>
            )}
            <button type="submit" disabled={loading} className="rm-btn rm-btn-primary w-full" data-testid="register-submit-btn">
              {loading ? "Hatching..." : "Create account"}
            </button>
          </form>
          <div className="mt-6 text-sm font-mono-rm">
            Already have an account?{" "}
            <Link to="/login" className="font-bold underline" data-testid="register-to-login-link">
              Login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
